import { useState, useCallback, useEffect, ChangeEvent, useMemo } from "react";
import { FiPlus, FiMessageSquare, FiAlertCircle, FiTrash2 } from "react-icons/fi";
import { useToast } from "@chakra-ui/react";
import { cn, showToast } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { Button } from "newui/components/Button";
import Modal from "newui/components/ui/Modal/Modal";
import { TableColumnFilter } from "newui/components/Filters";
import {
	useMyClarifications,
	useCreateClarification,
	useUpdateClarification,
	useDeleteClarification,
} from "../../../../../hooks/usePanelScoringQueries";
import type { PanelClarification, CreatePanelClarificationPayload, UpdatePanelClarificationPayload } from "../../../../../types/scoring.type";
import { SelectOption, SelectWithLabel, TextAreaWithLabel } from "newui/components/ui/InputWithLabel";
import TextBoxHTML from "newui/components/TextBoxHTML";

interface EvaluationClarificationsTableProps {
	rfxId: number;
	evalId: number;
	suppliers: Array<{ inviteId: number; supplierName: string }>;
	autoOpenForSupplierId?: number | null;
	onAutoOpenHandled?: () => void;
}

interface ClarificationFormState {
	rfx_invite_id: number | "";
	clarification_text: string;
	relates_to: string;
}

// Status ID constants matching backend ClarificationStatus model
const CLARIFICATION_STATUS = {
	DRAFT: 1,
	OPEN: 2,
	CLOSED: 3,
	PENDING: 4,
} as const;

const getStatusLabel = (clarification: PanelClarification): string => {
	if (clarification.is_approved) {
		return "Approved";
	}
	if (clarification.clarification_status_id === CLARIFICATION_STATUS.DRAFT) {
		return "Draft";
	}
	return "Pending Review";
};

const getStatusStyle = (clarification: PanelClarification): string => {
	if (clarification.is_approved) {
		return "bg-green-100 text-green-700";
	}
	if (clarification.clarification_status_id === CLARIFICATION_STATUS.DRAFT) {
		return "bg-gray-100 text-gray-600";
	}
	return "bg-amber-100 text-amber-700";
};

const formatDate = (dateStr: string | null): string => {
	if (!dateStr) {
		return "—";
	}
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

const ClarificationRow: React.FC<{
	clarification: PanelClarification;
	supplierName: string;
	onEdit: (clarification: PanelClarification) => void;
	onDelete: (clarification: PanelClarification) => void;
}> = ({ clarification, supplierName, onEdit, onDelete }) => {
	const isDraft = clarification.clarification_status_id === CLARIFICATION_STATUS.DRAFT;

	return (
		<tr
			className={cn(
				"border-b border-gray-100 transition-colors hover:bg-gray-50",
				isDraft && "cursor-pointer"
			)}
			onClick={() => {
				if (isDraft) {
					onEdit(clarification);
				}
			}}
			data-testid={`eval-clarification-row-${clarification.id}`}
		>
			<td className="px-3 py-3 text-sm text-gray-500 font-bold max-w-[300px]">
				{supplierName}
			</td>
			<td className="px-3 py-3 text-sm text-gray-600">
				<TextBoxHTML className="line-clamp-2" html={clarification.clarification_text} />
			</td>
			<td className="px-3 py-3 text-sm text-gray-500">
				{clarification.relates_to || "—"}
			</td>
			<td className="px-3 py-3">
				<span
					className={cn(
						"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
						getStatusStyle(clarification)
					)}
				>
					{getStatusLabel(clarification)}
				</span>
			</td>
			<td className="px-3 py-3 text-sm text-gray-600">
				{clarification.created_by?.name || "—"}
			</td>
			<td className="px-3 py-3 text-xs text-gray-500">
				{formatDate(clarification.created_at)}
			</td>
			<td className="px-3 py-3">
				{isDraft && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onDelete(clarification);
						}}
						className="inline-flex items-center rounded p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
						data-testid={`delete-clarification-btn-${clarification.id}`}
					>
						<FiTrash2 size={14} />
					</button>
				)}
			</td>
		</tr>
	);
};

export const EvaluationClarificationsTable: React.FC<EvaluationClarificationsTableProps> = ({
	rfxId,
	evalId,
	suppliers,
	autoOpenForSupplierId,
	onAutoOpenHandled,
}) => {
	const { data: clarifications, isLoading, error } = useMyClarifications(rfxId, evalId);
	const createMutation = useCreateClarification(rfxId, evalId);
	const updateMutation = useUpdateClarification(rfxId, evalId);
	const deleteMutation = useDeleteClarification(rfxId, evalId);
	const toast = useToast();
	const [showForm, setShowForm] = useState(false);
	const [editingClarification, setEditingClarification] = useState<PanelClarification | null>(null);
	const [deletingClarification, setDeletingClarification] = useState<PanelClarification | null>(null);
	const [supplierFilters, setSupplierFilters] = useState<number[]>([]);
	const [form, setForm] = useState<ClarificationFormState>({
		rfx_invite_id: suppliers.length > 0 ? suppliers[0].inviteId : "",
		clarification_text: "",
		relates_to: "",
	});

	const resetForm = useCallback((keepSupplier = false) => {
		setForm((prev) => ({
			rfx_invite_id: keepSupplier ? prev.rfx_invite_id : (suppliers.length > 0 ? suppliers[0].inviteId : ""),
			clarification_text: "",
			relates_to: "",
		}));
		setEditingClarification(null);
	}, [suppliers]);

	useEffect(() => {
		if (autoOpenForSupplierId != null) {
			setForm({
				rfx_invite_id: autoOpenForSupplierId,
				clarification_text: "",
				relates_to: "",
			});
			setEditingClarification(null);
			setShowForm(true);
			onAutoOpenHandled?.();
		}
	}, [autoOpenForSupplierId, onAutoOpenHandled]);

	const openCreateForm = useCallback(() => {
		resetForm();
		setShowForm(true);
	}, [resetForm]);

	const openEditForm = useCallback((clarification: PanelClarification) => {
		setForm({
			rfx_invite_id: clarification.rfx_invite_id,
			clarification_text: clarification.clarification_text,
			relates_to: clarification.relates_to || "",
		});
		setEditingClarification(clarification);
		setShowForm(true);
	}, []);

	const closeForm = useCallback(() => {
		setShowForm(false);
		setEditingClarification(null);
	}, []);

	const buildCreatePayload = useCallback((): CreatePanelClarificationPayload | null => {
		if (!form.rfx_invite_id || !form.clarification_text.trim()) {
			return null;
		}
		return {
			rfx_invite_id: Number(form.rfx_invite_id),
			clarification_text: form.clarification_text.trim(),
			relates_to: form.relates_to.trim() || undefined,
		};
	}, [form]);

	const handleSubmit = useCallback(() => {
		if (editingClarification) {
			if (!form.rfx_invite_id || !form.clarification_text.trim()) {
				return;
			}
			const payload: UpdatePanelClarificationPayload = {
				rfx_invite_id: Number(form.rfx_invite_id),
				clarification_text: form.clarification_text.trim(),
				relates_to: form.relates_to.trim() || null,
			};
			updateMutation.mutate(
				{ clarificationId: editingClarification.id, payload },
				{
					onSuccess: () => {
						toast({ title: "Clarification updated", status: "success", duration: 4000, isClosable: true });
						closeForm();
						resetForm();
					},
				}
			);
		} else {
			const payload = buildCreatePayload();
			if (!payload) {
				return;
			}
			createMutation.mutate(payload, {
				onSuccess: () => {
					showToast("Clarification created", "Success", true);
					closeForm();
					resetForm();
				},
			});
		}
	}, [form, editingClarification, createMutation, updateMutation, closeForm, resetForm, buildCreatePayload, toast]);

	const handleSubmitAndAnother = useCallback(() => {
		const payload = buildCreatePayload();
		if (!payload) {
			return;
		}
		createMutation.mutate(payload, {
			onSuccess: () => {
				showToast("Clarification created", "Success", true);
				resetForm(true);
				setTimeout(() => {
					document.getElementById("clar-text")?.focus();
				}, 100);
			},
		});
	}, [buildCreatePayload, createMutation, resetForm, toast]);

	const handleConfirmDelete = useCallback(() => {
		if (!deletingClarification) {
			return;
		}
		deleteMutation.mutate(deletingClarification.id, {
			onSuccess: () => {
				showToast("Clarification deleted", "Success", true);
				setDeletingClarification(null);
			},
		});
	}, [deletingClarification, deleteMutation]);

	const getSupplierName = (inviteId: number): string => {
		return suppliers.find((s) => s.inviteId === inviteId)?.supplierName ?? "Unknown";
	};

	const onSupplierChanged = useCallback((e: SelectOption) => {
		setForm((prev) => ({ ...prev, rfx_invite_id: Number(e.value) }));
	}, []);

	const onQuestionChanged = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		setForm((prev) => ({ ...prev, clarification_text: e.target.value }));
	}, []);

	const onRelatesChanged = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		setForm((prev) => ({ ...prev, relates_to: e.target.value }));
	}, []);

	const supplierOptions = useMemo(() =>
		suppliers.map((supplier) => ({
			value: supplier.inviteId,
			label: supplier.supplierName,
		} as SelectOption)),
		[suppliers]
	);

	const supplierFilterOptions = useMemo(() =>
		suppliers.map((s) => ({
			value: s.inviteId,
			label: s.supplierName,
		})),
		[suppliers]
	);

	const items = useMemo(() => clarifications ?? [], [clarifications]);

	const filteredItems = useMemo(() => {
		if (supplierFilters.length === 0) {
			return items;
		}
		return items.filter((c) => supplierFilters.includes(c.rfx_invite_id));
	}, [items, supplierFilters]);

	if (isLoading) {
		return (
			<div className="space-y-2" data-testid="eval-clarifications-loading">
				{[1, 2].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="40px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	if (error) {
		return (
			<p className="text-sm text-red-600" data-testid="eval-clarifications-error">
				Failed to load clarifications.
			</p>
		);
	}

	const activeMutation = editingClarification ? updateMutation : createMutation;
	const formTitle = editingClarification ? "Edit Clarification" : "Raise Clarification";
	const submitLabel = activeMutation.isPending
		? "Saving..."
		: editingClarification ? "Save" : "Submit";

	const thClass = "px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500";

	return (
		<div data-testid="evaluation-clarifications-table">
			<div className="mb-3">
				<div className="flex items-center gap-2 mb-2">
					<FiMessageSquare size={16} className="text-gray-400" aria-hidden="true" />
					<h4 className="text-sm font-medium text-gray-700">
						My Clarifications
					</h4>
					{items.length > 0 && (
						<span className="text-xs text-gray-400">
							({items.length})
						</span>
					)}
				</div>
				<Button
					btntype="primary"
					size="xs"
					onClick={openCreateForm}
					data-testid="add-clarification-btn"
				>
					<FiPlus size={14} aria-hidden="true" />
					Add Clarification
				</Button>
			</div>

			<p className="mb-3 text-xs text-gray-500">
				Clarifications raised here are visible only to you and the RFx Lead.
				They must be approved before being sent to suppliers.
			</p>

			{items.length === 0 ? (
				<div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-400 text-sm">
					No clarifications raised yet. Use the button above to propose a clarification question.
				</div>
			) : (
				<div className="w-full overflow-x-auto">
					<table className="w-full min-w-[700px] border-collapse text-sm">
						<thead>
							<tr className="border-b border-gray-200">
								<th className="text-left">
									<TableColumnFilter<number>
										label="Supplier"
										align="start"
										className={thClass}
										options={supplierFilterOptions}
										value={supplierFilters}
										onChange={setSupplierFilters}
										isMulti
									/>
								</th>
								<th className={thClass}>Question</th>
								<th className={thClass}>Relates To</th>
								<th className={thClass}>Status</th>
								<th className={thClass}>Created By</th>
								<th className={thClass}>Created</th>
								<th className="w-10"></th>
							</tr>
						</thead>
						<tbody>
							{filteredItems.map((c) => (
								<ClarificationRow
									key={c.id}
									clarification={c}
									supplierName={getSupplierName(c.rfx_invite_id)}
									onEdit={openEditForm}
									onDelete={setDeletingClarification}
								/>
							))}
						</tbody>
					</table>
					{filteredItems.length === 0 && items.length > 0 && (
						<div className="py-6 text-center text-sm text-gray-400">
							No clarifications match the selected filter.
						</div>
					)}
				</div>
			)}

			{/* Add/Edit clarification form modal */}
			{showForm && (
				<Modal
					isOpen={showForm}
					onClose={closeForm}
					title={formTitle}
					size="md"
					modalType="standard"
					iconType={editingClarification ? "edit" : "create"}
					closeOnEsc
					closeOnOverlayClick={false}
					trapFocus={false}
					{...(editingClarification
						? {
							primaryButton: {
								text: submitLabel,
								onClick: handleSubmit,
								disabled: !form.rfx_invite_id || !form.clarification_text.trim() || activeMutation.isPending,
							},
							secondaryButton: {
								text: "Cancel",
								onClick: closeForm,
							},
						}
						: {
							customFooter: (
								<div className="flex w-full justify-between gap-2">
									<Button
										btntype="primary"
										size="sm"
										onClick={handleSubmitAndAnother}
										disabled={!form.rfx_invite_id || !form.clarification_text.trim() || createMutation.isPending}
									>
										Submit and Create Another
									</Button>
									<div className="flex gap-2">
										<Button
											btntype="secondary"
											size="sm"
											onClick={closeForm}
										>
											Cancel
										</Button>
										<Button
											btntype="secondary"
											size="sm"
											onClick={handleSubmit}
											disabled={!form.rfx_invite_id || !form.clarification_text.trim() || createMutation.isPending}
										>
											{submitLabel}
										</Button>
									</div>
								</div>
							),
						}
					)}
				>
					<div className="space-y-4">
						{activeMutation.isError && (
							<div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
								<FiAlertCircle size={14} className="shrink-0 text-red-500" />
								<p className="text-xs text-red-700">
									{editingClarification
										? "Failed to update clarification. Please try again."
										: "Failed to create clarification. Please try again."
									}
								</p>
							</div>
						)}

						<div>
							<SelectWithLabel
								id="clar-supplier"
								label="Supplier"
								onChange={onSupplierChanged}
								options={supplierOptions}
								value={form.rfx_invite_id as number}
								data-testid="clar-supplier-select"
								required
							/>
						</div>

						<div>
							<TextAreaWithLabel
								label="Relates to"
								id="clar-relates"
								value={form.relates_to}
								onChange={onRelatesChanged}
								placeholder="e.g., Section 3.2, Pricing Schedule"
								maxLength={255}
								data-testid="clar-relates-input"
								rows={2}
							/>
						</div>

						<div>
							<TextAreaWithLabel
								label="Clarification question"
								id="clar-text"
								value={form.clarification_text}
								onChange={onQuestionChanged}
								placeholder="Describe the clarification you need from this supplier..."
								rows={4}
								maxLength={2000}
								required
							/>
						</div>
					</div>
				</Modal>
			)}

			{/* Delete confirmation modal */}
			{deletingClarification && (
				<Modal
					isOpen={!!deletingClarification}
					onClose={() => setDeletingClarification(null)}
					title="Delete Clarification"
					size="sm"
					modalType="standard"
					iconType="warning"
					closeOnEsc
					closeOnOverlayClick
					primaryButton={{
						text: deleteMutation.isPending ? "Deleting..." : "Delete",
						onClick: handleConfirmDelete,
						btntype: "primary",
						disabled: deleteMutation.isPending,
					}}
					secondaryButton={{
						text: "Cancel",
						onClick: () => setDeletingClarification(null),
					}}
				>
					<p className="text-sm text-gray-600 text-center">
						Are you sure you want to delete this draft clarification? This action cannot be undone.
					</p>
				</Modal>
			)}
		</div>
	);
};
